import type {
  FeedbackAnswer,
  FeedbackQuestion,
  FormDefinition,
  QuestionType,
  SubmittedAnswerRequest,
} from './types';
import {NOTE_MAX_LENGTH} from './types';

// Form state, keyed by stable questionId / option value — never display label.

export type SingleAnswer = {
  kind: 'single';
  value: string | null;
  note: string;
};
export type MultiAnswer = {
  kind: 'multi';
  // option value -> follow-up note ("" when the option has no follow-up)
  selections: Record<string, string>;
};
export type RatingAnswer = {kind: 'rating'; value: number | null};
export type TextAnswer = {kind: 'text'; text: string};

export type QuestionAnswer =
  SingleAnswer | MultiAnswer | RatingAnswer | TextAnswer;

export type FeedbackFormState = Record<string, QuestionAnswer>;

type AnswerKind = QuestionAnswer['kind'];

export function answerKind(type: QuestionType): AnswerKind {
  switch (type) {
    case 'boolean':
    case 'segmented-list':
      return 'single';
    case 'multi-select':
      return 'multi';
    case 'rating':
      return 'rating';
    case 'text-field':
    case 'text-area':
      return 'text';
  }
}

function emptyAnswer(type: QuestionType): QuestionAnswer {
  switch (answerKind(type)) {
    case 'single':
      return {kind: 'single', value: null, note: ''};
    case 'multi':
      return {kind: 'multi', selections: {}};
    case 'rating':
      return {kind: 'rating', value: null};
    case 'text':
      return {kind: 'text', text: ''};
  }
}

export function initialFeedbackState(
  definition: FormDefinition,
  prior?: Partial<FeedbackFormState>,
): FeedbackFormState {
  const state: FeedbackFormState = {};
  for (const question of definition.questions) {
    state[question.id] = prior?.[question.id] ?? emptyAnswer(question.type);
  }
  return state;
}

function optionByValue(question: FeedbackQuestion, value: string) {
  if (!('fields' in question)) return undefined;
  return question.fields.find((field) => field.value === value);
}

// Inline validation errors keyed by questionId. An empty map means the form is
// valid and Save is allowed.
export function validateFeedback(
  definition: FormDefinition,
  state: FeedbackFormState,
): Record<string, string> {
  const errors: Record<string, string> = {};

  const fail = (id: string, message: string) => {
    if (!errors[id]) errors[id] = message;
  };

  for (const question of definition.questions) {
    const answer = state[question.id];
    if (!answer) continue;
    const required = question.required ?? false;

    if (answer.kind === 'single') {
      if (required && answer.value === null) {
        fail(question.id, 'Please choose an option.');
        continue;
      }
      if (answer.value !== null) {
        const option = optionByValue(question, answer.value);
        if (
          option?.additionalTextField?.required &&
          answer.note.trim() === ''
        ) {
          fail(question.id, 'Please add the requested detail.');
        }
      }
      if (answer.note.length > NOTE_MAX_LENGTH) {
        fail(question.id, `Keep this under ${NOTE_MAX_LENGTH} characters.`);
      }
    } else if (answer.kind === 'multi' && question.type === 'multi-select') {
      const values = Object.keys(answer.selections);
      const min = question.minSelections ?? (required ? 1 : 0);
      const max = question.maxSelections ?? Number.POSITIVE_INFINITY;
      if (values.length < min) {
        fail(
          question.id,
          min === 1
            ? 'Please choose at least one option.'
            : `Please choose at least ${min} options.`,
        );
      } else if (values.length > max) {
        fail(question.id, `Please choose no more than ${max} options.`);
      }
      for (const [value, note] of Object.entries(answer.selections)) {
        const option = optionByValue(question, value);
        if (option?.additionalTextField?.required && note.trim() === '') {
          fail(question.id, 'Please add the requested detail.');
        }
        if (note.length > NOTE_MAX_LENGTH) {
          fail(question.id, `Keep notes under ${NOTE_MAX_LENGTH} characters.`);
        }
      }
    } else if (answer.kind === 'rating') {
      if (required && answer.value === null) {
        fail(question.id, 'Please choose a rating.');
      }
    } else if (answer.kind === 'text') {
      if (required && answer.text.trim() === '') {
        fail(question.id, 'This field is required.');
      }
      if (answer.text.length > NOTE_MAX_LENGTH) {
        fail(question.id, `Keep this under ${NOTE_MAX_LENGTH} characters.`);
      }
    }
  }

  return errors;
}

// Flatten the state into one answer entry per selected option, resolving each
// option's fieldId from the definition.
export function toAnswers(
  definition: FormDefinition,
  state: FeedbackFormState,
): SubmittedAnswerRequest[] {
  const answers: SubmittedAnswerRequest[] = [];

  for (const question of definition.questions) {
    const answer = state[question.id];
    if (!answer) continue;

    if (answer.kind === 'single') {
      if (answer.value === null) continue;
      const option = optionByValue(question, answer.value);
      answers.push({
        questionId: question.id,
        fieldId: option?.id,
        value: answer.value,
        ...(answer.note.trim() !== '' ? {note: answer.note} : {}),
      });
    } else if (answer.kind === 'multi') {
      for (const [value, note] of Object.entries(answer.selections)) {
        const option = optionByValue(question, value);
        answers.push({
          questionId: question.id,
          fieldId: option?.id,
          value,
          ...(note.trim() !== '' ? {note} : {}),
        });
      }
    } else if (answer.kind === 'rating') {
      if (answer.value === null) continue;
      answers.push({questionId: question.id, value: String(answer.value)});
    } else if (answer.kind === 'text') {
      if (answer.text.trim() === '') continue;
      answers.push({questionId: question.id, note: answer.text});
    }
  }

  return answers;
}

// The inverse of toAnswers: rebuild form state from a prior submission to pre-fill
// the edit flow. Answers group by questionId; unknown questionIds are ignored.
export function fromAnswers(
  definition: FormDefinition,
  answers: FeedbackAnswer[],
): FeedbackFormState {
  const byQuestion = new Map<string, FeedbackAnswer[]>();
  for (const answer of answers) {
    const group = byQuestion.get(answer.questionId);
    if (group) group.push(answer);
    else byQuestion.set(answer.questionId, [answer]);
  }

  const state: FeedbackFormState = {};
  for (const question of definition.questions) {
    const kind = answerKind(question.type);
    const entries = byQuestion.get(question.id) ?? [];
    const [first] = entries;

    if (kind === 'single') {
      state[question.id] = {
        kind: 'single',
        value: first?.value ?? null,
        note: first?.note ?? '',
      };
    } else if (kind === 'multi') {
      const selections: Record<string, string> = {};
      for (const entry of entries) {
        if (entry.value !== undefined) {
          selections[entry.value] = entry.note ?? '';
        }
      }
      state[question.id] = {kind: 'multi', selections};
    } else if (kind === 'rating') {
      // Fall back to null (unanswered) for a missing or non-numeric value so a
      // bad value can't slip past the required check or serialize as "NaN".
      const rating = first?.value != null ? Number(first.value) : Number.NaN;
      state[question.id] = {
        kind: 'rating',
        value: Number.isFinite(rating) ? rating : null,
      };
    } else {
      state[question.id] = {kind: 'text', text: first?.note ?? ''};
    }
  }

  return state;
}
